/* eslint-disable prefer-promise-reject-errors */

import HttpStatusConst from "constants/HttpStatusConst";
import SessionConst from "constants/SessionConst";
import 'whatwg-fetch';
import { getAccessTokenFromSession, getSessionItem } from "./SessionHelper";

/**
 * Fetches JSON using fetch API and proper error handling
 *
 * @param {*} args
 * @returns {Promise}
 */
export const fetchJSON = (...args) =>
  window.fetch(...args).then(handleResponse((response) => {
    return response.json()
  })).catch(error => {
    // if (isEmptyArray(error.messages)) {
    // return Promise.reject({
    //   messages: [{ code: 'UNAUTHORIZED' }]
    // })
    // } else {
    throw error
    // }
  });

/**
 * creates a function for fetch promise response and error handling
 *
 * @param {Function} callback
 * @returns {Function}
 */
const handleResponse = (callback) => (response) => {

  if (response.status === HttpStatusConst.HTTP_STATUS_NO_CONTENT) {
    return response;
  } else if (response.status === HttpStatusConst.HTTP_STATUS_CREATED) {
    return {}
  } else if (response.status === HttpStatusConst.HTTP_STATUS_UNAUTHORIZED) {
    return Promise.reject({
      messages: [{ code: 'UNAUTHORIZED' }]
    })
  }

  if (response.ok) {
    return callback(response);
  }

  // Only return the json if response.json() returns it
  if (response.headers?.get('content-type')?.indexOf('application/json') !== -1) {
    return response.json().then((response) => Promise.reject(response));
  }

  // Otherwise, reject so we can capture status, statusType, type, etc.
  return Promise.reject(response);
};

const getHeaders = () => {
  return new Headers({
    'Content-Type': 'application/json',
    'X-ChoiceEdge-Requestor': getSessionItem(SessionConst.REQUESTOR),
    'X-ChoiceEdge-TrackingId': getSessionItem(SessionConst.TRACKING_ID),
    'Authorization': 'Bearer ' + getAccessTokenFromSession()
  })
}

/**
 * Common secure DELETE settings
 */
export const secureDELETE = (hasBody= false) => {
  return {
    credentials: 'same-origin',
    headers: new Headers({
      'Content-Type': hasBody ? 'application/json' : 'application/x-www-form-urlencoded',
      'X-ChoiceEdge-Requestor': getSessionItem(SessionConst.REQUESTOR),
      'X-ChoiceEdge-TrackingId': getSessionItem(SessionConst.TRACKING_ID),
      'Authorization': 'Bearer ' + getAccessTokenFromSession()
    }),
    method: 'DELETE',
  }
}

/**
 * Common secure GET settings
 */
export const secureGET = () => {
  return {
    credentials: 'same-origin',
    headers: getHeaders(),
    method: 'GET'
  }
}

/**
 * Common secure POST settings
 */
export const securePOST = () => {
  return {
    credentials: 'same-origin',
    headers: getHeaders(),
    method: 'POST',
  }
}

/**
 * Common secure PUT settings
 */
export const securePUT = () => {
  return {
    credentials: 'same-origin',
    headers: getHeaders(),
    method: 'PUT'
  }
}

/**
 * Generates an encoded query string
 *
 * @param {Object} query
 * @returns {String} URL-encoded string
 */
export const urlEncode = (query) =>
  Object.keys(query)
    .reduce(
      (result, param) =>
        result.concat(
          Array.isArray(query[param])
            ? query[param] ? urlEncodeParam(param)(query[param].join(',')) : urlEncodeParam(param)(query[param])
            : urlEncodeParam(param)(query[param])
        ),
      []
    )
    .join('&');

/**
 * Creates urlEncoded string that is deep referencing. ex: {foo: {baz: bar}} will return foo.baz=bar
 *
 * @param {Object} obj
 * @param {String} prefix
 * @returns {String} URL-encoded string
 */
export const urlEncodeDeepObject = (obj, prefix) => {
  const formParms = [];
  prefix = prefix || '';
  Object.keys(obj)
    .sort()
    .forEach((key) => {
      const subObj = obj[key];
      if (Object(subObj) === subObj) {
        if (Object.getOwnPropertyNames(subObj).length) {
          formParms.push(urlEncodeDeepObject(subObj, `${prefix + key}.`));
        }
      } else {
        formParms.push(`${encodeURIComponent(prefix + key)}=${encodeURIComponent(subObj)}`);
      }
    });
  return formParms.join('&');
};

/**
 * creates function to handle encoding values with specified key
 *
 * @param {String} param
 * @returns {Function}
 */
const urlEncodeParam = (param) => (value) =>
  `${encodeURIComponent(param)}=${value}`;

/**
 * Generates a url with an encoded query string
 *
 * @param {String} url
 * @param {Object} query
 * @returns {String} Encode query string
 */
export const withQuery = (url, query) =>
  `${url}${url.indexOf('?') < 0 ? '?' : '&'}${urlEncode(query)}`;
